import { useState, type FormEvent } from 'react';
import { messageDErreur, useSession } from '../auth/session';
import { Alerte, Bouton, Carte, Champ } from '../ui';

/**
 * Demande de réinitialisation du mot de passe.
 *
 * Cet écran manquait, alors que le serveur savait déjà le faire : `POST
 * /auth/password/forgot` et l'e-mail qui va avec existent depuis T-013, mais aucune page
 * n'y menait. Un parent qui oubliait son mot de passe n'avait tout simplement pas de porte
 * de sortie — et l'historique de ses enfants derrière.
 *
 * **La réponse est la même quelle que soit l'adresse saisie.** Le serveur répond toujours
 * 202, et l'écran affiche toujours le même message. Distinguer les deux cas transformerait
 * ce formulaire en outil pour savoir si son ex-conjoint a un compte chez nous — dans un
 * produit destiné à des gens en conflit, l'information n'est pas anodine.
 *
 * Le ton reste factuel : perdre son mot de passe n'est pas une faute, et rien ici ne le
 * présente comme telle.
 */
export function MotDePasseOublie({ surRetour }: { surRetour?: () => void }) {
  const { client } = useSession();
  const [email, setEmail] = useState('');
  const [envoye, setEnvoye] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  async function soumettre(evenement: FormEvent<HTMLFormElement>): Promise<void> {
    evenement.preventDefault();
    setErreur(null);
    setEnvoi(true);
    try {
      await client.request('/auth/password/forgot', {
        method: 'POST',
        body: { email: email.trim() },
        // Aucune session à renouveler ici : un 401 n'aurait aucun sens sur cette route.
        skipRefresh: true,
      });
      setEnvoye(true);
    } catch (cause) {
      setErreur(messageDErreur(cause));
    } finally {
      setEnvoi(false);
    }
  }

  if (envoye) {
    return (
      <Carte titre="Regardez votre boîte aux lettres">
        <p style={{ marginTop: 0 }}>
          Si un compte existe pour <strong>{email.trim()}</strong>, un lien de
          réinitialisation vient d'y être envoyé. Il est valable une heure et ne fonctionne
          qu'une fois.
        </p>
        <p className="discret">
          Rien reçu ? Vérifiez les indésirables, puis réessayez dans quelques minutes.
        </p>
        <Bouton variante="secondaire" onClick={() => surRetour?.()}>
          Revenir à la connexion
        </Bouton>
      </Carte>
    );
  }

  return (
    <Carte titre="Mot de passe oublié">
      <p style={{ marginTop: 0 }}>
        Indiquez l'adresse de votre compte : nous vous envoyons un lien pour en choisir un
        nouveau.
      </p>

      <form onSubmit={(evenement) => void soumettre(evenement)} noValidate>
        <Champ
          label="Adresse e-mail"
          type="email"
          name="email"
          autoComplete="email"
          required
          value={email}
          onChange={(evenement) => setEmail(evenement.target.value)}
        />

        {erreur ? <Alerte titre="La demande n’a pas abouti">{erreur}</Alerte> : null}

        <div className="actions-empilees">
          <Bouton type="submit" disabled={!email.trim() || envoi}>
            {envoi ? 'Envoi en cours…' : 'Envoyer le lien'}
          </Bouton>
          <Bouton variante="lien" onClick={() => surRetour?.()}>
            Revenir à la connexion
          </Bouton>
        </div>
      </form>
    </Carte>
  );
}
